import {
  useGetInventorySwingStatusQuery,
  useInventorySwingTickNowMutation,
  useInventorySwingSelfTuneNowMutation,
  useGetSettingsQuery,
  usePatchSettingsMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

/**
 * InventorySwingCard — sells slices of held inventory near range-high,
 * buys back near range-low. Captures chop revenue using existing
 * inventory instead of USDT capital. Self-tunes daily via Gemini Pro.
 *
 * Sections:
 *  1. Engine state (enabled/regime/aggregate P&L)
 *  2. Per-asset state table — what each asset is doing right now
 *  3. Recent cycle log — last 20 closed/abandoned cycles with P&L
 */
export function InventorySwingCard() {
  const q = useGetInventorySwingStatusQuery(undefined, { pollingInterval: 30000 });
  const settings = useGetSettingsQuery();
  const [patchSettings] = usePatchSettingsMutation();
  const [tickNow, tickState] = useInventorySwingTickNowMutation();
  const [tuneNow, tuneState] = useInventorySwingSelfTuneNowMutation();

  const enabled = (settings.data as any)?.inventory_swing_enabled === true;
  const slicePct = (settings.data as any)?.inventory_swing_slice_pct ?? 15.0;
  const atrMult = (settings.data as any)?.inventory_swing_band_atr_mult ?? 2.0;
  const timeStopDays = (settings.data as any)?.inventory_swing_time_stop_days ?? 7;

  const states: any[] = q.data?.per_asset_states || [];
  const cycles: any[] = q.data?.recent_cycles || [];
  const agg = q.data?.aggregate || {};
  const totalCycles = Number(agg.n ?? 0);
  const wins = Number(agg.wins ?? 0);
  const totalPnl = Number(agg.total_pnl ?? 0);
  const winRate = totalCycles > 0 ? (wins / totalCycles) * 100 : 0;

  async function toggleEnabled() {
    try {
      await patchSettings({ inventory_swing_enabled: !enabled }).unwrap();
      toast.success(`Inventory swing ${!enabled ? "enabled" : "paused"}`);
      q.refetch();
      settings.refetch();
    } catch (e: any) {
      toast.error(`toggle failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  async function onTickNow() {
    try {
      const r: any = await tickNow().unwrap();
      const actions = (r?.results || []).filter((x: any) => x.action !== "noop").length;
      if (r?.status === "skipped_regime_risk_on") {
        toast.info("Skipped — regime is risk_on (engine sleeps during trends)");
      } else if (actions === 0) {
        toast.success(`Tick OK — ${r?.candidates_count ?? 0} candidates, no entries (all in middle zone)`);
      } else {
        toast.success(`Tick OK — ${actions} action(s) across ${r?.candidates_count ?? 0} candidates`);
      }
      q.refetch();
    } catch (e: any) {
      toast.error(`tick failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  async function onTuneNow() {
    try {
      const r: any = await tuneNow().unwrap();
      if (r?.status === "not_enough_data") {
        toast.info(`Need ${r?.min ?? 5} cycles to tune (have ${r?.cycles ?? 0})`);
      } else if (r?.status === "tuned") {
        toast.success(`Auto-tune applied ${r.applied?.length ?? 0} change(s)`);
      } else {
        toast.info(`Tune: ${r?.status} — ${r?.rationale ?? ""}`);
      }
      settings.refetch();
    } catch (e: any) {
      toast.error(`tune failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span>Inventory swing</span>
          {enabled ? (
            <Badge tone="good">enabled</Badge>
          ) : (
            <Badge tone="muted">paused</Badge>
          )}
          <span className="text-xs text-muted font-normal">
            sell slice near range-high · buy back near range-low · self-tunes daily
          </span>
        </CardTitle>
        <div className="flex gap-2 items-center flex-wrap">
          <span className="text-[10px] text-muted">
            slice {slicePct}% · ±{atrMult}×ATR · time-stop {timeStopDays}d
          </span>
          <Button size="sm" variant="ghost" onClick={onTickNow} disabled={tickState.isLoading}>
            {tickState.isLoading ? "..." : "Tick now"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onTuneNow} disabled={tuneState.isLoading}>
            {tuneState.isLoading ? "..." : "Tune"}
          </Button>
          <Button size="sm" onClick={toggleEnabled} variant={enabled ? "danger" : "default"}>
            {enabled ? "Pause" : "Enable"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Aggregate */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Cycles" value={String(totalCycles)} />
          <Tile label="Win rate"
                value={totalCycles ? `${winRate.toFixed(0)}%` : "—"}
                tone={winRate >= 60 ? "good" : winRate >= 40 ? "warn" : "muted"} />
          <Tile label="Total P&L"
                value={totalCycles ? `${totalPnl >= 0 ? "+" : ""}$${totalPnl.toFixed(4)}` : "—"}
                tone={totalPnl > 0 ? "good" : totalPnl < 0 ? "bad" : "muted"} />
          <Tile label="Active swings"
                value={String(states.filter(s => s.state === "swing_sold").length)} />
        </div>

        {/* Per-asset state */}
        <div>
          <div className="font-semibold text-xs mb-2">Per-asset state</div>
          {q.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : states.length === 0 ? (
            <div className="text-xs text-muted italic">
              No active state yet. Engine populates rows the first time it sees each asset.
              {enabled && " First scan runs on the next 30-min tick."}
            </div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Symbol</TH>
                  <TH>State</TH>
                  <TH className="text-right">Slice qty</TH>
                  <TH className="text-right">Sell px</TH>
                  <TH className="text-right">Buyback tgt</TH>
                  <TH className="text-right">Cycles</TH>
                  <TH className="text-right">Cum P&L</TH>
                  <TH>Note</TH>
                </TR>
              </THead>
              <TBody>
                {states.map((s) => (
                  <TR key={s.id}>
                    <TD className="font-mono">{s.symbol}</TD>
                    <TD>
                      <Badge tone={
                        s.state === "swing_sold" ? "warn" :
                        s.state === "idle" ? "muted" :
                        s.state === "abandoned" ? "bad" : "muted"
                      }>{s.state}</Badge>
                    </TD>
                    <TD className="text-right tabular text-xs">
                      {s.slice_qty ? Number(s.slice_qty).toFixed(6) : <span className="text-muted">—</span>}
                    </TD>
                    <TD className="text-right tabular text-xs">
                      {s.slice_sell_price ? `$${Number(s.slice_sell_price).toFixed(4)}` : <span className="text-muted">—</span>}
                    </TD>
                    <TD className="text-right tabular text-xs">
                      {s.slice_buy_target ? `$${Number(s.slice_buy_target).toFixed(4)}` : <span className="text-muted">—</span>}
                    </TD>
                    <TD className="text-right tabular">{s.total_cycles_completed ?? 0}</TD>
                    <TD className={`text-right tabular font-semibold ${
                      Number(s.total_pnl_usd ?? 0) > 0 ? "text-green-500" :
                      Number(s.total_pnl_usd ?? 0) < 0 ? "text-red-500" : "text-muted"
                    }`}>
                      {s.total_pnl_usd != null ? `${Number(s.total_pnl_usd) >= 0 ? "+" : ""}$${Number(s.total_pnl_usd).toFixed(4)}` : "—"}
                    </TD>
                    <TD className="text-xs text-muted">
                      {(s.note || "").slice(0, 60)}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </div>

        {/* Recent cycles */}
        <div>
          <div className="font-semibold text-xs mb-2">
            Recent cycles ({cycles.length})
          </div>
          {cycles.length === 0 ? (
            <div className="text-xs text-muted italic">
              No cycles yet. First swing fires when an asset's price enters its sell zone (upper ATR-band).
            </div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Symbol</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Sell</TH>
                  <TH className="text-right">Buy</TH>
                  <TH className="text-right">Qty extracted</TH>
                  <TH className="text-right">Net P&L</TH>
                  <TH>Started</TH>
                </TR>
              </THead>
              <TBody>
                {cycles.slice(0, 20).map((c) => (
                  <TR key={c.id}>
                    <TD className="font-mono">{c.symbol}</TD>
                    <TD>
                      <Badge tone={
                        c.status === "closed" ? "good" :
                        c.status === "abandoned" ? "warn" :
                        c.status === "open" ? "info" : "muted"
                      }>{c.status}</Badge>
                    </TD>
                    <TD className="text-right tabular text-xs">
                      {c.sell_price ? `$${Number(c.sell_price).toFixed(4)}` : <span className="text-muted">—</span>}
                    </TD>
                    <TD className="text-right tabular text-xs">
                      {c.buy_price ? `$${Number(c.buy_price).toFixed(4)}` : <span className="text-muted">—</span>}
                    </TD>
                    <TD className={`text-right tabular text-xs ${
                      Number(c.qty_extracted ?? 0) > 0 ? "text-green-500" : "text-muted"
                    }`}>
                      {c.qty_extracted != null ? `${Number(c.qty_extracted) >= 0 ? "+" : ""}${Number(c.qty_extracted).toFixed(6)}` : "—"}
                    </TD>
                    <TD className={`text-right tabular font-semibold ${
                      Number(c.net_pnl_usd ?? 0) > 0 ? "text-green-500" :
                      Number(c.net_pnl_usd ?? 0) < 0 ? "text-red-500" : "text-muted"
                    }`}>
                      {c.net_pnl_usd != null ? `${Number(c.net_pnl_usd) >= 0 ? "+" : ""}$${Number(c.net_pnl_usd).toFixed(4)}` : "—"}
                    </TD>
                    <TD className="text-muted text-xs">
                      {c.started_at ? new Date(c.started_at).toISOString().slice(5, 16) : "—"}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: "good"|"warn"|"bad"|"muted" }) {
  return (
    <div className="border border-border rounded p-2 bg-bg2">
      <div className="text-xs text-muted">{label}</div>
      <div className="font-semibold mt-0.5">
        {tone ? <Badge tone={tone}>{value}</Badge> : <span>{value}</span>}
      </div>
    </div>
  );
}
